import { useState } from "react";
import { Link } from "react-router-dom";
import { Globe, Menu, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import logo from "@/assets/logo.png";
import { translations, t, type Lang } from "@/lib/translations";

import { serviceAreas } from "@/data/service-areas";

interface NavbarProps {
  lang: Lang;
  onToggleLang: () => void;
  onOpenForm: () => void;
  /** Links that use anchor scroll on current page (e.g. #services) */
  hashLinks?: { label: string; href: string }[];
}

type DropdownKey = "services" | "work" | "areas" | "resources" | null;

export default function Navbar({ lang, onToggleLang, onOpenForm, hashLinks = [] }: NavbarProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<DropdownKey>(null);

  const T = translations;

  const ourWorkLabel = t(T.nav.ourWork, lang);
  const viewAllLabel = lang === "en" ? "View All Projects" : "Ver Todos los Proyectos";
  const areasLabel = t(T.nav.areas, lang);
  const viewAllAreasLabel = lang === "en" ? "View All Areas" : "Ver Todas las Áreas";

  const servicesItems = [
    { to: "/adu-builders-san-diego", label: t(T.nav.aduBuilder, lang) },
    { to: "/detached-adu-san-diego", label: lang === "en" ? "Detached ADU" : "ADU Independiente" },
    { to: "/attached-adu-san-diego", label: lang === "en" ? "Attached ADU" : "ADU Adosada" },
    { to: "/garage-conversion-adu-san-diego", label: t(T.nav.garageConversion, lang) },
  ];

  const floorPlansLabel = t(T.floorPlans.navLabel, lang);

  const resourcesItems = [
    { to: "/blog", label: t(T.nav.blog, lang) },
    { to: "/roi-calculator", label: t(T.nav.roiCalculator, lang) },
    { to: "/adu-financing-san-diego", label: t(T.nav.aduFinancing, lang) },
    { to: "/blog/adu-cost-san-diego-2026", label: t(T.nav.aduCostGuide, lang) },
    { to: "/events", label: t(T.nav.events, lang) },
    { to: "/recursos/glosario", label: t(T.nav.glossary, lang) },
  ];

  const closeMobile = () => setMobileMenuOpen(false);

  const Dropdown = ({
    id,
    triggerTo,
    triggerLabel,
    children,
    width = "w-64",
  }: {
    id: Exclude<DropdownKey, null>;
    triggerTo?: string;
    triggerLabel: string;
    children: React.ReactNode;
    width?: string;
  }) => {
    const isOpen = openDropdown === id;
    return (
      <div
        className="relative"
        onMouseEnter={() => setOpenDropdown(id)}
        onMouseLeave={() => setOpenDropdown(null)}
      >
        {triggerTo ? (
          <Link
            to={triggerTo}
            className="text-sm font-medium text-foreground/70 hover:text-foreground transition-colors inline-flex items-center gap-1"
          >
            {triggerLabel}
            <ChevronDown className="h-3.5 w-3.5" />
          </Link>
        ) : (
          <button
            type="button"
            className="text-sm font-medium text-foreground/70 hover:text-foreground transition-colors inline-flex items-center gap-1"
          >
            {triggerLabel}
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
        )}
        {isOpen && (
          <div className={`absolute top-full left-0 pt-2 ${width} z-50`}>
            <div className="bg-white rounded-lg shadow-lg border border-border py-2">{children}</div>
          </div>
        )}
      </div>
    );
  };

  return (
    <header className="fixed top-0 inset-x-0 z-40 bg-white/95 backdrop-blur-md shadow-sm border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/">
          <img src={logo} alt="EG Construction" className="h-9" width={125} height={36} />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-5">
          {hashLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm font-medium text-foreground/70 hover:text-foreground transition-colors"
            >
              {l.label}
            </a>
          ))}

          <Dropdown id="services" triggerLabel={t(T.nav.services, lang)}>
            {servicesItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="block px-4 py-2 text-sm text-foreground/80 hover:bg-muted hover:text-foreground transition-colors"
                onClick={() => setOpenDropdown(null)}
              >
                {item.label}
              </Link>
            ))}
          </Dropdown>

          <Link
            to="/floor-plans"
            className="text-sm font-medium text-foreground/70 hover:text-foreground transition-colors"
          >
            {floorPlansLabel}
          </Link>

          <Link
            to="/projects"
            className="text-sm font-medium text-foreground/70 hover:text-foreground transition-colors"
          >
            {ourWorkLabel}
          </Link>


          <Dropdown id="areas" triggerTo="/areas" triggerLabel={areasLabel}>
            <Link
              to="/areas"
              className="block px-4 py-2 text-sm font-semibold text-foreground hover:bg-muted transition-colors"
              onClick={() => setOpenDropdown(null)}
            >
              {viewAllAreasLabel}
            </Link>
            <div className="h-px bg-border mx-2 my-1" />
            {serviceAreas.map((area) => (
              <Link
                key={area.slug}
                to={`/areas/${area.slug}`}
                className="block px-4 py-2 text-sm text-foreground/80 hover:bg-muted hover:text-foreground transition-colors"
                onClick={() => setOpenDropdown(null)}
              >
                {area.city[lang]}
              </Link>
            ))}
          </Dropdown>

          <Dropdown id="resources" triggerLabel={t(T.nav.resources, lang)} width="w-56">
            {resourcesItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="block px-4 py-2 text-sm text-foreground/80 hover:bg-muted hover:text-foreground transition-colors"
                onClick={() => setOpenDropdown(null)}
              >
                {item.label}
              </Link>
            ))}
          </Dropdown>
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={onToggleLang}
            className="flex items-center gap-1.5 text-sm font-medium text-foreground/70 hover:text-foreground transition-colors"
            aria-label={lang === "en" ? "ES – Switch to Spanish" : "EN – Cambiar a inglés"}
          >
            <Globe className="h-4 w-4" />
            <span className="uppercase font-bold">{lang === "en" ? "ES" : "EN"}</span>
          </button>

          <Button
            onClick={onOpenForm}
            className="hidden md:inline-flex h-9 px-4 text-sm font-heading font-bold bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {t(T.nav.freeConsultation, lang)}
          </Button>
          <button
            className="md:hidden text-foreground"
            onClick={() => setMobileMenuOpen((v) => !v)}
            aria-label={lang === "en" ? "Open menu" : "Abrir menú"}
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-border px-4 py-4 flex flex-col gap-4 max-h-[calc(100vh-4rem)] overflow-y-auto">
          <Link to="/" className="text-sm font-medium text-foreground/80 hover:text-foreground" onClick={closeMobile}>
            {t(T.nav.home, lang)}
          </Link>

          {hashLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm font-medium text-foreground/80 hover:text-foreground"
              onClick={closeMobile}
            >
              {l.label}
            </a>
          ))}

          {/* Services */}
          <div className="text-sm font-semibold text-foreground">{t(T.nav.services, lang)}</div>
          {servicesItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="text-sm text-foreground/70 hover:text-foreground pl-3 border-l-2 border-border"
              onClick={closeMobile}
            >
              {item.label}
            </Link>
          ))}

          <div className="h-px bg-border my-1" />

          {/* Floor Plans */}
          <Link to="/floor-plans" className="text-sm font-semibold text-foreground hover:text-foreground" onClick={closeMobile}>
            {floorPlansLabel}
          </Link>

          <div className="h-px bg-border my-1" />

          {/* Our Work */}
          <Link to="/projects" className="text-sm font-semibold text-foreground hover:text-foreground" onClick={closeMobile}>
            {ourWorkLabel}
          </Link>


          <div className="h-px bg-border my-1" />

          {/* Areas */}
          <Link to="/areas" className="text-sm font-semibold text-foreground hover:text-foreground" onClick={closeMobile}>
            {areasLabel}
          </Link>
          {serviceAreas.map((area) => (
            <Link
              key={area.slug}
              to={`/areas/${area.slug}`}
              className="text-sm text-foreground/70 hover:text-foreground pl-3 border-l-2 border-border"
              onClick={closeMobile}
            >
              {area.city[lang]}
            </Link>
          ))}

          <div className="h-px bg-border my-1" />

          {/* Resources */}
          <div className="text-sm font-semibold text-foreground">{t(T.nav.resources, lang)}</div>
          {resourcesItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="text-sm text-foreground/70 hover:text-foreground pl-3 border-l-2 border-border"
              onClick={closeMobile}
            >
              {item.label}
            </Link>
          ))}

          <Button
            onClick={() => {
              onOpenForm();
              closeMobile();
            }}
            className="w-full h-10 text-sm font-heading font-bold bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {t(T.nav.freeConsultation, lang)}
          </Button>
        </div>
      )}
    </header>
  );
}
